import { useEffect, useMemo, useState } from "react";
import api from "api/axiosInstance";
import styles from "../AdminPanel.module.scss";

type ClinicInfoRow = {
  id: number;
  name: string;
  detail: string;
};

type TechItem = {
  title: string;
  text: string;
};

const KEY_TECH_TITLE = "home.technologies.title";
const KEY_TECH_LEAD = "home.technologies.lead";
const KEY_TECH_ITEMS = "home.technologies.items";

const DEFAULT_TITLE = "Оборудование и технологии";
const DEFAULT_LEAD =
  "Наш подход — сочетание опыта врачей и современных технологий, чтобы лечение было точным, понятным и спокойным.";

const DEFAULT_ITEMS: TechItem[] = [
  {
    title: "Лечение под микроскопом",
    text: "Кариес и корневые каналы лечим под увеличением — точнее и безопаснее.",
  },
  {
    title: "Фотопротокол",
    text: "Фиксируем этапы лечения фото — понятный контроль и прозрачность.",
  },
  {
    title: "3D‑сканирование",
    text: "Цифровые слепки — комфортнее и точнее традиционных.",
  },
  {
    title: "Профгигиена по протоколу",
    text: "Бережно и аккуратно подбираем режим под ваши зубы и дёсны.",
  },
  {
    title: "Цифровое планирование",
    text: "План лечения и результат — понятны заранее.",
  },
  {
    title: "Комфорт и спокойствие",
    text: "Без спешки: объясняем, показываем, делаем по шагам.",
  },
];

function safeParseItems(detail: string | undefined | null): TechItem[] | null {
  const raw = (detail ?? "").trim();
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;

    const items: TechItem[] = parsed
      .filter((x: any) => x && typeof x === "object")
      .map((x: any) => ({
        title: typeof x.title === "string" ? x.title.trim() : "",
        text: typeof x.text === "string" ? x.text.trim() : "",
      }))
      .filter((x) => x.title && x.text);

    return items.length ? items : null;
  } catch {
    return null;
  }
}

function toJsonDetail(items: TechItem[]) {
  return JSON.stringify(
    items
      .map((x) => ({
        title: (x.title || "").trim(),
        text: (x.text || "").trim(),
      }))
      .filter((x) => x.title && x.text)
  );
}

function extractErr(e: any) {
  const status = e?.response?.status;
  const data = e?.response?.data;

  const msg =
    typeof data === "string"
      ? data
      : data?.message
      ? Array.isArray(data.message)
        ? data.message.join(", ")
        : String(data.message)
      : data?.error
      ? String(data.error)
      : e?.message;

  return status ? `${msg} (HTTP ${status})` : msg || "Неизвестная ошибка";
}

export default function TechnologiesSettings() {
  const [rows, setRows] = useState<ClinicInfoRow[]>([]);
  const byKey = useMemo(() => {
    const m = new Map<string, ClinicInfoRow>();
    rows.forEach((r) => m.set(r.name, r));
    return m;
  }, [rows]);

  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  // Поля формы
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [lead, setLead] = useState(DEFAULT_LEAD);
  const [items, setItems] = useState<TechItem[]>(DEFAULT_ITEMS);

  const load = async () => {
    setLoading(true);
    setErrorText(null);
    try {
      const res = await api.get<ClinicInfoRow[]>("/clinic-info");
      setRows(res.data ?? []);
    } catch (e: any) {
      setErrorText(`Ошибка загрузки: ${extractErr(e)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Подтягиваем значения из БД
  useEffect(() => {
    const t = byKey.get(KEY_TECH_TITLE)?.detail;
    const l = byKey.get(KEY_TECH_LEAD)?.detail;
    const it = byKey.get(KEY_TECH_ITEMS)?.detail;

    setTitle((t || "").trim() || DEFAULT_TITLE);
    setLead((l || "").trim() || DEFAULT_LEAD);

    const parsed = safeParseItems(it);
    setItems(parsed || DEFAULT_ITEMS);
  }, [byKey]);

  // Надёжный upsert: delete + create (как в ContactsMapSettings)
  const upsert = async (name: string, detail: string) => {
    const existing = byKey.get(name);
    if (existing) {
      await api.delete(`/clinic-info/${existing.id}`);
    }
    await api.post("/clinic-info", { name, detail });
  };

  const validateItems = (list: TechItem[]) => {
    const cleaned = list
      .map((x) => ({ title: (x.title || "").trim(), text: (x.text || "").trim() }))
      .filter((x) => x.title && x.text);

    if (cleaned.length === 0) return { ok: false as const, cleaned: [] as TechItem[] };

    // Если у пользователя есть пустые карточки — не молча выкидываем, а просим поправить
    if (cleaned.length !== list.length) return { ok: false as const, cleaned };

    return { ok: true as const, cleaned };
  };

  const saveAll = async () => {
    setLoading(true);
    setErrorText(null);
    try {
      const cleanTitle = (title || "").trim() || DEFAULT_TITLE;
      const cleanLead = (lead || "").trim() || DEFAULT_LEAD;

      const v = validateItems(items);
      if (!v.ok) {
        setErrorText("Проверьте карточки: у каждой должны быть заполнены «Название» и «Описание».");
        setLoading(false);
        return;
      }

      await upsert(KEY_TECH_TITLE, cleanTitle);
      await upsert(KEY_TECH_LEAD, cleanLead);
      await upsert(KEY_TECH_ITEMS, toJsonDetail(v.cleaned));

      await load();
    } catch (e: any) {
      setErrorText(`Ошибка сохранения: ${extractErr(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems((prev) => [...prev, { title: "", text: "" }]);
  };

  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const moveItem = (from: number, to: number) => {
    setItems((prev) => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [it] = next.splice(from, 1);
      next.splice(to, 0, it);
      return next;
    });
  };

  const updateItem = (idx: number, patch: Partial<TechItem>) => {
    setItems((prev) => prev.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  };

  return (
    <div className={styles.form_block}>
      <h3>Технологии (главная)</h3>

      {errorText ? <div style={{ color: "#c00", marginBottom: 12 }}>{errorText}</div> : null}

      <label>Заголовок</label>
      <input value={title} onChange={(e) => setTitle(e.target.value)} />

      <label>Текст (lead)</label>
      <textarea
        value={lead}
        onChange={(e) => setLead(e.target.value)}
        rows={4}
        style={{ width: "60%" }}
      />

      <hr style={{ margin: "10px 0 16px" }} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>Карточки</h3>
        <button className={styles.btn_primary} type="button" onClick={addItem} disabled={loading}>
          + Добавить
        </button>
      </div>

      <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
        {items.map((it, idx) => (
          <div
            key={idx}
            style={{
              border: "1px solid #e3e3e3",
              borderRadius: 8,
              padding: 12,
              background: "#fff",
            }}
          >
            <div style={{ display: "flex", gap: 10, justifyContent: "space-between" }}>
              <div style={{ flex: 1 }}>
                <label>Название</label>
                <input
                  value={it.title}
                  onChange={(e) => updateItem(idx, { title: e.target.value })}
                  placeholder="Например: 3D‑сканирование"
                  style={{ width: "60%" }}
                />

                <label>Описание</label>
                <textarea
                  value={it.text}
                  onChange={(e) => updateItem(idx, { text: e.target.value })}
                  rows={3}
                  style={{ width: "80%" }}
                  placeholder="Короткое пояснение (1–2 предложения)"
                />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button
                  className={styles.btn}
                  type="button"
                  onClick={() => moveItem(idx, idx - 1)}
                  disabled={loading || idx === 0}
                >
                  ↑
                </button>
                <button
                  className={styles.btn}
                  type="button"
                  onClick={() => moveItem(idx, idx + 1)}
                  disabled={loading || idx === items.length - 1}
                >
                  ↓
                </button>
                <button
                  className={styles.btn_danger}
                  type="button"
                  onClick={() => removeItem(idx)}
                  disabled={loading}
                >
                  Удалить
                </button>
              </div>
            </div>
          </div>
        ))}

        {items.length === 0 ? (
          <div style={{ opacity: 0.7 }}>Пока нет карточек. Нажмите «Добавить».</div>
        ) : null}
      </div>

      <div style={{ marginTop: 12 }}>
        <button className={styles.btn_primary} onClick={saveAll} disabled={loading}>
          {loading ? "Сохраняю..." : "Сохранить"}
        </button>
      </div>
    </div>
  );
}